// ProductDetail.jsx - React Bootstrap Version
import React, { useState, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Form, Table } from "react-bootstrap";
import {
  BsCart2,
  BsHeart,
  BsHeartFill,
  BsStar,
  BsStarFill,
  BsStarHalf,
  BsX,
  BsShop,
  BsChatDots,
} from "react-icons/bs";

import { FaInstagram, FaWhatsapp } from "react-icons/fa";

import { MdPayment } from "react-icons/md";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import MyNavbar from "../../general/mynavbar/MyNavbar";
import useScrollToTop from "../../../contexts/UseScrolltoTop";
import "../../../assets/css/customer/components/ProductDetailPage.css";
import axios from "axios";
import AppContext from "../../../contexts/AppContext";
import LoadingScreen from "../../../components/general/animations/Animations.js";

// ── Address validation ───────────────────────────────────────────
// Uses AppContext's regExpChecker("required", ...) for the "field is
// empty" case, then applies the field-specific format rule on top.
const validateAddress = (address, regExpChecker) => {
  const errors = {};

  // regExpChecker("required", ...) from AppContext only tells us WHETHER
  // the field is empty — we still show our own field-specific label
  // instead of its generic "This field is required" text.
  if (regExpChecker("required", address.name)) {
    errors.name = "Full Name is required";
  } else if (address.name.trim().length < 3) {
    errors.name = "Full name kam az kam 3 characters ka hona chahiye";
  }

  if (regExpChecker("required", address.phone)) {
    errors.phone = "Phone Number is required";
  } else {
    const cleanPhone = address.phone.replace(/[-\s]/g, "");
    if (!/^[0-9]{10,13}$/.test(cleanPhone)) {
      errors.phone = "Valid phone number enter karein (10-13 digits)";
    }
  }

  if (regExpChecker("required", address.area)) {
    errors.area = "Street / Area is required";
  } else if (address.area.trim().length < 5) {
    errors.area = "Pura address likhein (street, area, etc.)";
  }

  return errors;
};

// ════════════════════════════════════════════════════════════════
const ProductDetail = () => {
  const {
    baseUrl,
    customer,
    selectedProductForCustomer,
    baseUrlForFiles,
    incrementUnread,
    setSelectedShop,
    customerAuthToken,
    regExpChecker,
  } = useContext(AppContext);

  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState("");
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [activeTab, setActiveTab] = useState("reviews");
  const [productReviews, setProductReviews] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [address, setAddress] = useState({
    name: "",
    phone: "",
    area: "",
  });
  const [addressErrors, setAddressErrors] = useState({});

  // Ref to the "Delivery Address" section inside the checkout modal so we
  // can auto-scroll it into view whenever validation fails.
  const addressSectionRef = useRef(null);
  const nameInputRef = useRef(null);

  useScrollToTop();

  useEffect(() => {
    if (!selectedProductForCustomer) {
      navigate("/categories");
    } else {
      const timer = setTimeout(() => setLoading(false), 2500);

      const formdata = new FormData();
      formdata.append("product_id", selectedProductForCustomer.id);
      axios
        .post(`${baseUrl}/customer/review/getProductReviews`, formdata, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${customerAuthToken}`,
          },
        })
        .then((res) => {
          if (res.data.success) setProductReviews(res.data.reviews);
        })
        .catch(() => {});

      return () => clearTimeout(timer);
    }
  }, [selectedProductForCustomer]);

  if (loading) {
    return <LoadingScreen />;
  }

  if (showSuccess) {
    return (
      <LoadingScreen type="success" onComplete={() => navigate("/orders")} />
    );
  }

  if (!selectedProductForCustomer) return null;

  const product = selectedProductForCustomer;
  const price = product.price;
  const subtotal = price * quantity;
  const shippingFee = 200;
  const orderTotal = price * quantity + shippingFee;
  const isOutOfStock = Number(product.stock_quantity) <= 0;

  // Review images can come as plain path strings or {image_path} objects
  const getReviewImagePath = (img) =>
    typeof img === "string" ? img : img?.image_path;

  // ── Add to Cart API call ─────────────────────────────────────

  const addToCartAPI = (customerId) => {
    const formdata = new FormData();
    formdata.append("chcustomer_id", customerId);
    formdata.append("chproduct_id", product.id);
    formdata.append("price", price);
    formdata.append("color", selectedColor);
    formdata.append("size", selectedSize);
    formdata.append("subtotal", subtotal);
    formdata.append("quantity", quantity);
    formdata.append("isPurchased", 0);
    formdata.append("isCustomized", 0);

    axios
      .post(`${baseUrl}/customer/cart/addToCart`, formdata, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${customerAuthToken}`,
        },
      })
      .then(() => {
        incrementUnread("cart");
        toast.success("Added to Cart");
      })
      .catch(() => toast.error("Failed to add to cart"));
  };

  const handleAddToCart = () => {
    if (isOutOfStock) {
      toast.error("This product is out of stock");
      return;
    }

    const customer = JSON.parse(localStorage.getItem("loggedInCustomer"));
    const customerId = customer?.id;

    if (!customerId) {
      toast.error("Please login first!");
      navigate("/login");
      return;
    }

    if (product.variant?.colors && product.variant.colors.trim() !== "") {
      if (selectedColor === null || selectedColor === undefined) {
        toast.warning("Please select a color!");
        return;
      }
    }

    if (product.variant?.sizes && product.variant.sizes.trim() !== "") {
      if (!selectedSize) {
        toast.warning("Please select a size!");
        return;
      }
    }

    const formdata = new FormData();
    formdata.append("chcustomer_id", customerId);

    axios
      .post(`${baseUrl}/customer/cart/viewCart`, formdata, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${customerAuthToken}`,
        },
      })
      .then((res) => {
        const existingCart = res.data.carts || [];
        const cartSellerId = existingCart[0]?.product?.seller_id;
        const newSellerId = product?.seller_id;

        if (existingCart.length > 0 && cartSellerId !== newSellerId) {
          Promise.all(
            existingCart.map((item) => {
              const fd = new FormData();
              fd.append("chcart_id", item.id);
              return axios.post(`${baseUrl}/customer/cart/deleteCart`, fd, {
                headers: {
                  "Content-Type": "multipart/form-data",
                  Authorization: `Bearer ${customerAuthToken}`,
                },
              });
            }),
          ).then(() => addToCartAPI(customerId));
        } else {
          addToCartAPI(customerId);
        }
      })
      .catch(() => addToCartAPI(customerId));
  };

  const AddToWishlist = (productId) => {
    const customerId = customer?.id;
    if (!customerId) {
      toast.error("Please login first!");
      navigate("/login");
      return;
    }

    const formData = new FormData();
    formData.append("customer_id", customerId);
    formData.append("product_id", productId);

    axios
      .post(`${baseUrl}/customer/wishlist/addToWishlist`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${customerAuthToken}`,
        },
      })
      .then((response) => {
        if (response.data.success) {
          toast.success(response.data.msg);
          setIsWishlisted(true);
          incrementUnread("favourites");
        } else {
          toast.warning(response.data.msg);
        }
      })
      .catch((error) => {
        console.log("Error:", error);
        toast.error("Something went wrong!");
      });
  };

  const handleBuyNow = () => {
    if (isOutOfStock) {
      toast.error("This product is out of stock");
      return;
    }

    const customer = JSON.parse(localStorage.getItem("loggedInCustomer"));
    if (!customer?.id) {
      toast.error("Please login first!");
      navigate("/login");
      return;
    }

    if (product.variant?.colors && product.variant.colors.trim() !== "") {
      if (selectedColor === null || selectedColor === undefined) {
        toast.warning("Please select a color!");
        return;
      }
    }

    if (product.variant?.sizes && product.variant.sizes.trim() !== "") {
      if (!selectedSize) {
        toast.warning("Please select a size!");
        return;
      }
    }

    setShowModal(true);
  };

  const handleQuantityChange = (type) => {
    if (type === "increment" && quantity < product.stock_quantity)
      setQuantity(quantity + 1);
    else if (type === "decrement" && quantity > 1) setQuantity(quantity - 1);
  };

  const handleChatWithSeller = () =>
    navigate("/product/chat-seller", { state: { selectedProductForCustomer } });

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    for (let i = 0; i < fullStars; i++)
      stars.push(<BsStarFill key={i} className="star filled" />);
    if (hasHalfStar)
      stars.push(<BsStarHalf key="half" className="star filled" />);
    while (stars.length < 5)
      stars.push(<BsStar key={stars.length} className="star" />);
    return stars;
  };

  const averageRating =
    productReviews.length > 0
      ? productReviews.reduce((sum, r) => sum + Number(r.rating), 0) /
        productReviews.length
      : 0;

  const ratingDistribution = [5, 4, 3, 2, 1].map((star) => {
    const totalReviews = productReviews.length;
    const countForStar = productReviews.filter(
      (r) => Math.round(r.rating) === star,
    ).length;
    const percentage =
      totalReviews > 0 ? Math.round((countForStar / totalReviews) * 100) : 0;
    return { stars: star, percentage };
  });

  // Share links — sirf Instagram aur WhatsApp
  const productShareUrl =
    typeof window !== "undefined" ? window.location.href : "";

  const socialLinks = [
    {
      icon: <FaInstagram />,
      url: "https://www.instagram.com/crafthive__?igsh=MTVwOHd6ZTZqdncxOQ==",
      name: "Instagram",
    },
    {
      icon: <FaWhatsapp />,
      url: `https://wa.me/?text=${encodeURIComponent(
        `Check out this product: ${product.product_name} - ${productShareUrl}`,
      )}`,
      name: "WhatsApp",
    },
  ];

  // ── Proceed to Pay handler ───────────────────────────────────
  // Validates the address, and on failure:
  //   1) resets isPlacingOrder so the button never gets stuck, and
  //   2) smooth-scrolls the modal so the Delivery Address section
  //      slides into view automatically.
  const handleProceedToPay = () => {
    if (isPlacingOrder) return;
    setIsPlacingOrder(true);

    const errors = validateAddress(address, regExpChecker);
    if (Object.keys(errors).length > 0) {
      setAddressErrors(errors);
      setIsPlacingOrder(false);

      // Slide the checkout modal so the address section is visible,
      // then focus the first invalid field.
      if (addressSectionRef.current) {
        addressSectionRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      if (errors.name && nameInputRef.current) {
        nameInputRef.current.focus();
      }
      return;
    }
    setAddressErrors({});

    const customerData = JSON.parse(
      localStorage.getItem("loggedInCustomer"),
    );
    const customerId = customerData?.id;

    const cartFormdata = new FormData();
    cartFormdata.append("chcustomer_id", customerId);
    cartFormdata.append("chproduct_id", product.id);
    cartFormdata.append("price", price);
    cartFormdata.append("color", selectedColor);
    cartFormdata.append("size", selectedSize);
    cartFormdata.append("subtotal", subtotal);
    cartFormdata.append("quantity", quantity);
    cartFormdata.append("isPurchased", 0);
    cartFormdata.append("isCustomized", 0);

    axios
      .post(`${baseUrl}/customer/cart/addToCart`, cartFormdata, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${customerAuthToken}`,
        },
      })
      .then(() => {
        const formdata = new FormData();
        formdata.append("chcustomer_id", customerId);
        formdata.append("seller_id", product.seller_id);
        formdata.append("full_name", address.name);
        formdata.append("phone", address.phone);
        formdata.append("address", address.area);
        formdata.append("total_amount", orderTotal);
        formdata.append("shipping_fee", shippingFee);
        formdata.append("payment_method", "COD");
        formdata.append("status", "Processing");

        axios
          .post(`${baseUrl}/customer/order/createOrder`, formdata, {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${customerAuthToken}`,
            },
          })
          .then((res) => {
            if (res.data.success) {
              setShowModal(false);
              incrementUnread("notifications");
              setShowSuccess(true);
            } else {
              toast.error("Order failed. Try again.");
            }
          })
          .catch(() => toast.error("Something went wrong."))
          .finally(() => setIsPlacingOrder(false));
      })
      .catch(() => {
        toast.error("Failed to add to cart.");
        setIsPlacingOrder(false);
      });
  };

  return (
    <div className="product-detail-page">
      <MyNavbar />
      <ToastContainer />

      <div className="breadcrumb-container">
        {/* <nav className="breadcrumb mb-0">
          <span onClick={() => navigate("/")} className="breadcrumb-link">
            Home
          </span>
          <span className="breadcrumb-separator">/</span>
          <span
            onClick={() => navigate("/categories")}
            className="breadcrumb-link"
          >
            {product.category?.category_name}
          </span>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">{product.product_name}</span>
        </nav> */}
      </div>

      <div className="product-main-section">
        <div className="product-images-col">
          <div className="main-image-box">
            {product.images[selectedImage] ? (
              <img
                src={`${baseUrlForFiles}/${product.images[selectedImage].image_path}`}
                alt={product.product_name}
              />
            ) : (
              <div className="no-image-placeholder">No Image</div>
            )}
          </div>
          <div className="thumbnail-row">
            {product.images?.map((img, index) => (
              <div
                key={index}
                className={`thumbnail-box ${selectedImage === index ? "active" : ""}`}
                onClick={() => setSelectedImage(index)}
              >
                <img
                  src={`${baseUrlForFiles}/${img.image_path}`}
                  alt={`Thumbnail ${index + 1}`}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="product-info-col">
          <h1 className="product-title">{product.product_name}</h1>

          <div className="price-box">
            <span className="current-price">Rs {product.price}</span>
            {/* {isOutOfStock && (
              <span className="ov-out-of-stock-badge">Out of Stock</span>
            )} */}
          </div>
          {!isOutOfStock && product.estimated_delivery && (
  <div className="delivery-estimate">
    <strong>Estimated Delivery:</strong> {product.estimated_delivery?.range_text}
  </div>
)}

          <p className="product-desc">{product.description}</p>

          <Button
            className="chat-seller-btn w-100"
            onClick={handleChatWithSeller}
          >
            <BsChatDots size={20} />
            Customize Your Product
          </Button>

          {product.variant?.colors && product.variant.colors.trim() !== "" && (
            <div className="option-group">
              <Form.Label className="fw-semibold">Color:</Form.Label>
              <div className="color-options">
                {product.variant.colors
                  .split(",")
                  .filter(Boolean)
                  .map((color, index) => (
                    <div
                      key={index}
                      className={`color-circle ${selectedColor === index ? "active" : ""}`}
                      style={{ backgroundColor: color }}
                      onClick={() => {
                        setSelectedColor(index);
                        if (product.images && product.images[index]) {
                          setSelectedImage(index);
                        }
                      }}
                    />
                  ))}
              </div>
            </div>
          )}

          {product.variant?.sizes && product.variant.sizes.trim() !== "" && (
            <div className="option-group">
              <Form.Label className="fw-semibold">
                Size: {selectedSize}
              </Form.Label>
              <div className="size-options">
                {product.variant.sizes
                  .split(",")
                  .filter(Boolean)
                  .map((size) => (
                    <button
                      key={size}
                      className={`size-btn ${selectedSize === size ? "active" : ""}`}
                      onClick={() => setSelectedSize(size)}
                    >
                      {size}
                    </button>
                  ))}
              </div>
            </div>
          )}

          <div className="option-group">
            <Form.Label className="fw-semibold">Quantity:</Form.Label>
            <div className="quantity-row">
              <div className="quantity-box">
                <button
                  onClick={() => handleQuantityChange("decrement")}
                  disabled={isOutOfStock}
                >
                  -
                </button>
                <span>{quantity}</span>
                <button
                  onClick={() => handleQuantityChange("increment")}
                  disabled={isOutOfStock}
                >
                  +
                </button>
              </div>
              {!isOutOfStock && (
                <span className="stock-text">
                  Only {product.stock_quantity} items left!
                </span>
              )}
            </div>
          </div>

          <div className="action-btns">
            <button className="btn-add-cart" onClick={handleAddToCart}>
              <BsCart2 size={20} /> Add to Cart
            </button>
            <button className="btn-buy" onClick={handleBuyNow}>
              Buy Now
            </button>
            <button
              className={`btn-wishlist-icon ${isWishlisted ? "active" : ""}`}
              onClick={() => AddToWishlist(product.id)}
            >
              {isWishlisted ? <BsHeartFill size={22} /> : <BsHeart size={22} />}
            </button>
          </div>

          <div className="product-meta">
            <p>
              <strong>Category:</strong> {product.category?.category_name}
            </p>
          </div>

          <div className="social-share">
            <strong>Share:</strong>
            <div className="social-icons">
              {socialLinks.map((social, index) => (
                <span
                  key={index}
                  aria-label={social.name}
                  onClick={() => window.open(social.url, "_blank")}
                >
                  {social.icon}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="product-tabs-section">
        <div className="tabs-header">
          {["description", "reviews"].map((tab) => (
            <button
              key={tab}
              className={`tab-btn ${activeTab === tab ? "active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab === "description" ? "Description" : "Review"}
            </button>
          ))}
        </div>

        <div className="tabs-content">
          {activeTab === "reviews" && (
            <div className="reviews-section">
              <div className="rating-summary">
                <div className="rating-score">
                  <h2>{averageRating > 0 ? averageRating.toFixed(1) : 0}</h2>
                  <span>out of 5</span>
                  <div
                    className="stars-row"
                    style={{ justifyContent: "center" }}
                  >
                    {renderStars(averageRating)}
                  </div>
                  <span className="total-reviews">
                    Based on {productReviews.length} Reviews
                  </span>
                </div>
                <div className="rating-bars">
                  {ratingDistribution.map((row) => (
                    <div key={row.stars} className="rating-bar-row">
                      <span>{row.stars} star</span>
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{ width: `${row.percentage}%` }}
                        />
                      </div>
                      <span>{row.percentage}%</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="reviews-list">
                {productReviews.length === 0 ? (
                  <p className="no-reviews">No reviews yet.</p>
                ) : (
                  productReviews.map((review) => (
                    <div key={review.id} className="review-item">
                      <div className="reviewer-info">
                        <div className="reviewer-avatar">
                          {review.customer?.first_name
                            ? review.customer.first_name.charAt(0).toUpperCase()
                            : review.customer?.email
                              ? review.customer.email.charAt(0).toUpperCase()
                              : "?"}
                        </div>
                        <div>
                          <h4>
                            {review.customer
                              ? review.customer.first_name &&
                                review.customer.last_name
                                ? `${review.customer.first_name} ${review.customer.last_name}`
                                : review.customer.email || "Anonymous"
                              : "Anonymous"}
                          </h4>
                          <span className="review-date">
                            {new Date(review.created_at).toLocaleDateString(
                              "en-PK",
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              },
                            )}
                          </span>
                        </div>
                      </div>
                      <div className="review-rating">
                        {renderStars(review.rating)}
                      </div>
                      <p className="review-text">{review.comment}</p>
                      {review.images?.length > 0 && (
                        <div className="review-images-row">
                          {review.images.map((img, idx) => {
                            const src = `${baseUrlForFiles}/${getReviewImagePath(img)}`;
                            return (
                              <img
                                key={idx}
                                src={src}
                                alt={`Review ${idx + 1}`}
                                className="review-image-thumb"
                                onClick={() => setPreviewImage(src)}
                              />
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeTab === "description" && (
            <div className="description-content">
              <h3>Product Description</h3>
              <p>{product.description}</p>

              <Table bordered hover responsive className="mt-3">
                <tbody>
                  <tr>
                    <td>
                      <strong>Category</strong>
                    </td>
                    <td>{product.category?.category_name}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Stock</strong>
                    </td>
                    <td>
                      {isOutOfStock
                        ? "Out of Stock"
                        : `${product.stock_quantity} items`}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="cm-backdrop" onClick={() => setShowModal(false)}>
          <div className="cm-box" onClick={(e) => e.stopPropagation()}>
            <div className="cm-header">
              <h2 className="cm-title">Checkout</h2>
              <button className="cm-close" onClick={() => setShowModal(false)}>
                <BsX size={22} />
              </button>
            </div>

            <div className="cm-body">
              <div className="cm-section">
                <h3 className="cm-section-title">Your Item</h3>
                <div className="cm-items-list">
                  <div className="cm-item-row">
                    <img
                      src={`${baseUrlForFiles}/${product.images[0]?.image_path}`}
                      alt={product.product_name}
                      className="cm-item-img"
                    />
                    <div className="cm-item-info">
                      <p className="cm-item-name">{product.product_name}</p>
                      <div className="cm-item-meta">
                        {selectedSize && <span>Size: {selectedSize}</span>}
                        {product.variant?.colors &&
                          product.variant.colors.split(",")[selectedColor] && (
                            <span className="cm-color-wrap">
                              Color:
                              <span
                                className="cm-color-dot"
                                style={{
                                  backgroundColor:
                                    product.variant.colors.split(",")[
                                      selectedColor
                                    ],
                                }}
                              />
                            </span>
                          )}
                        <span>Qty: {quantity}</span>
                      </div>
                    </div>
                    <p className="cm-item-price">
                      Rs {(price * quantity).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              <div className="cm-section">
                <h3 className="cm-section-title">Order Summary</h3>
                <div className="cm-summary-box">
                  <div className="cm-summary-row">
                    <span>Subtotal</span>
                    <span>Rs {(price * quantity).toLocaleString()}</span>
                  </div>
                  <div className="cm-summary-row">
                    <span>Shipping Fee</span>
                    <span>Rs {shippingFee}</span>
                  </div>
                  <div className="cm-summary-divider" />
                  <div className="cm-summary-row cm-total">
                    <span>Total Amount</span>
                    <span>Rs {orderTotal.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className="cm-section">
                <h3 className="cm-section-title">Payment Method</h3>
                <div className="cm-cod-card">
                  <div className="cm-cod-icon">
                    <MdPayment size={24} color="#fff" />
                  </div>
                  <div className="cm-cod-info">
                    <p className="cm-cod-title">Cash on Delivery</p>
                    <p className="cm-cod-desc">
                      Pay in cash when your order arrives
                    </p>
                  </div>
                  <div className="cm-cod-check">✓</div>
                </div>
                <p className="cm-cod-note">
                  Online payment is not available. Only Cash on Delivery is
                  accepted.
                </p>
              </div>

              {/* Delivery Address section — ref is used so the modal can
                  auto-scroll here whenever validation fails on submit */}
              <div className="cm-section" ref={addressSectionRef}>
                <h3 className="cm-section-title">Delivery Address</h3>
                <div className="cm-addr-grid">
                  <div className="cm-field">
                    <label>Full Name</label>
                    <input
                      ref={nameInputRef}
                      name="name"
                      value={address.name}
                      onChange={(e) =>
                        setAddress({ ...address, name: e.target.value })
                      }
                      placeholder="e.g. Ali Hassan"
                      className={addressErrors.name ? "cm-input-err" : ""}
                    />
                    {addressErrors.name && (
                      <span className="cm-err">{addressErrors.name}</span>
                    )}
                  </div>
                  <div className="cm-field">
                    <label>Phone Number</label>
                    <input
                      name="phone"
                      value={address.phone}
                      onChange={(e) =>
                        setAddress({ ...address, phone: e.target.value })
                      }
                      placeholder="03XX-XXXXXXX"
                      className={addressErrors.phone ? "cm-input-err" : ""}
                    />
                    {addressErrors.phone && (
                      <span className="cm-err">{addressErrors.phone}</span>
                    )}
                  </div>
                  <div className="cm-field cm-field--full">
                    <label>Street / Area</label>
                    <input
                      name="area"
                      value={address.area}
                      onChange={(e) =>
                        setAddress({ ...address, area: e.target.value })
                      }
                      placeholder="Street no, Block, Colony..."
                      className={addressErrors.area ? "cm-input-err" : ""}
                    />
                    {addressErrors.area && (
                      <span className="cm-err">{addressErrors.area}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="cm-footer">
              <button
                className="cm-cancel-btn"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button
                className="cm-pay-btn"
                disabled={isPlacingOrder}
                onClick={handleProceedToPay}
              >
                {isPlacingOrder ? "Placing Order..." : "Proceed to Pay"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Review Image Lightbox ── */}
      {previewImage && (
        <div
          className="pdp-image-lightbox-overlay"
          onClick={() => setPreviewImage(null)}
        >
          <button
            className="pdp-image-lightbox-close"
            onClick={() => setPreviewImage(null)}
            aria-label="Close"
          >
            <BsX size={20} />
          </button>
          <img
            src={previewImage}
            alt="Review full size"
            className="pdp-image-lightbox-img"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* ── Sticky View Store Button ── */}

      <div
        className="sticky-store-btn"
        onClick={() => {
          setSelectedShop(product.seller);
          navigate("/shop-detail");
        }}
      >
        {product.seller?.shop_image ? (
          <img
            src={`${baseUrlForFiles}/${product.seller.shop_image}`}
            alt={product.seller?.store_name}
            className="sticky-store-img"
          />
        ) : (
          <div className="sticky-store-avatar">
            <BsShop size={18} />
          </div>
        )}
        <span>Visit Store</span>
      </div>
    </div>
  );
};

export default ProductDetail;